import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthHttpService } from 'src/services/auth-http.service';
import { AuthService } from 'src/services/auth.service';

export type AvatarUploadResult = {
  fileName: string;
  displayUrl: string;
};

type ReadAvatarJsonResponse = {
  resource?: {
    content?: string;
    mimeType?: string;
  };
};

type UploadAvatarResponse = {
  resource?: {
    files_uploaded?: Array<{
      fileName?: string;
      path?: string;
    }>;
  };
};

const DEFAULT_AVATAR_PATH = 'assets/icon/noavatar.jpg';
const AVATAR_MAX_UPLOAD_BYTES = 300 * 1024;
const AVATAR_MAX_DIMENSION_PX = 1024;
const AVATAR_MIN_DIMENSION_PX = 256;
const AVATAR_JPEG_QUALITIES = [0.92, 0.85, 0.78, 0.7, 0.62, 0.55, 0.48, 0.4, 0.32];

@Injectable({
  providedIn: 'root',
})
export class AvatarService {
  constructor(
    private authHttpService: AuthHttpService,
    private authService: AuthService,
    private http: HttpClient
  ) {}

  async getAvatar(filePath: string): Promise<string> {
    const trimmed = String(filePath ?? '').trim();
    if (!trimmed) {
      return DEFAULT_AVATAR_PATH;
    }

    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('data:') ||
      trimmed.startsWith('assets/')
    ) {
      return trimmed;
    }

    const url = `${environment.apiPilot}ExMEPublic?operation=readAvatar&filePath=${encodeURIComponent(trimmed)}`;

    try {
      const dataUrl = await this.fetchAuthenticatedAvatarDataUrl(url);
      if (dataUrl) {
        return dataUrl;
      }
    } catch (error) {
      console.error('getAvatar failed', error);
    }

    return DEFAULT_AVATAR_PATH;
  }

  async uploadAvatar(
    file: File | Blob,
    agentId: string | number
  ): Promise<AvatarUploadResult> {
    const uploadFile = await this.prepareUploadFile(file, agentId);
    const formData = new FormData();
    formData.append('content', uploadFile, uploadFile.name);
    formData.append('filePath', '');

    const url = `${environment.apiPilot}ExMEPublic?operation=uploadAvatar`;
    const resp = (await this.postUploadForm(url, formData)) as UploadAvatarResponse;

    const fileName = resp?.resource?.files_uploaded?.[0]?.fileName;
    if (!fileName) {
      throw new Error('Avatar upload did not return a file name');
    }

    const displayUrl = await this.getAvatar(fileName);
    return { fileName, displayUrl };
  }

  /** Capacitor native bridge only serializes File reliably in multipart bodies. */
  private async prepareUploadFile(
    file: File | Blob,
    agentId: string | number
  ): Promise<File> {
    const name = this.resolveAvatarUploadFileName(file, agentId);
    const source = Capacitor.isNativePlatform()
      ? await this.materializeBlob(file)
      : file;
    const compressed = await this.compressAvatarForUpload(source);
    const type = compressed.type || 'image/jpeg';

    if (
      !Capacitor.isNativePlatform() &&
      compressed instanceof File &&
      compressed.name
    ) {
      return compressed;
    }

    const buffer = await this.readAsArrayBuffer(compressed);
    return new File([buffer], name, { type });
  }

  /**
   * iOS/Android file inputs can return objects that pass instanceof File but
   * still fail FormData.append's Blob check — clone via ArrayBuffer first.
   */
  private async materializeBlob(source: File | Blob): Promise<Blob> {
    const buffer = await this.readAsArrayBuffer(source);
    const type = (source as Blob).type || 'image/jpeg';
    return new Blob([buffer], { type });
  }

  private readAsArrayBuffer(source: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
          return;
        }
        reject(new Error('Failed to read image data'));
      };
      reader.onerror = () =>
        reject(reader.error ?? new Error('Failed to read image data'));
      reader.readAsArrayBuffer(source);
    });
  }

  buildAvatarFileName(agentId: string | number): string {
    const today = new Date();
    const date =
      today.getFullYear() + '' + (today.getMonth() + 1) + today.getDate();
    const time =
      '' + today.getHours() + today.getMinutes() + today.getSeconds();
    return `avatar_${agentId}_${date}${time}.png`;
  }

  private resolveAvatarUploadFileName(
    file: File | Blob,
    agentId: string | number
  ): string {
    if (file instanceof File && file.name.trim()) {
      return file.name.replace(/\.(png|jpe?g|webp|gif)$/i, '.jpg');
    }

    return this.buildAvatarFileName(agentId).replace(/\.png$/i, '.jpg');
  }

  private async compressAvatarForUpload(file: File | Blob): Promise<Blob> {
    if (file.size <= AVATAR_MAX_UPLOAD_BYTES) {
      return file;
    }

    try {
      const image = await this.loadImageFromBlob(file);
      let bestBlob: Blob | null = null;

      for (
        let maxDimension = AVATAR_MAX_DIMENSION_PX;
        maxDimension >= AVATAR_MIN_DIMENSION_PX;
        maxDimension = Math.floor(maxDimension * 0.75)
      ) {
        for (const quality of AVATAR_JPEG_QUALITIES) {
          const candidate = await this.canvasToJpegBlob(
            image,
            maxDimension,
            quality
          );

          if (!bestBlob || candidate.size < bestBlob.size) {
            bestBlob = candidate;
          }

          if (candidate.size <= AVATAR_MAX_UPLOAD_BYTES) {
            return candidate;
          }
        }
      }

      if (bestBlob) {
        return bestBlob;
      }
    } catch (error) {
      console.warn('Avatar compression failed, uploading original file', error);
    }

    return file;
  }

  private loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };

      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to decode avatar image'));
      };

      image.src = url;
    });
  }

  private canvasToJpegBlob(
    image: HTMLImageElement,
    maxDimension: number,
    quality: number
  ): Promise<Blob> {
    const scale = Math.min(
      1,
      maxDimension / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height, 1)
    );
    const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      return Promise.reject(new Error('Canvas is not available'));
    }

    context.drawImage(image, 0, 0, width, height);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Avatar JPEG encoding failed'));
            return;
          }

          resolve(blob);
        },
        'image/jpeg',
        quality
      );
    });
  }

  /**
   * Authenticated readAvatar returns raw image bytes (e.g. JPEG), not JSON.
   * CapacitorHttp delivers binary responses as base64 when responseType is set.
   */
  private async fetchAuthenticatedAvatarDataUrl(
    url: string
  ): Promise<string | null> {
    for (const responseType of ['blob', 'arraybuffer'] as const) {
      try {
        const res = await this.authHttpService.authenticatedGetResponse({
          url,
          responseType,
        });

        if (res.status < 200 || res.status >= 300) {
          continue;
        }

        const raw = res.data;
        if (typeof raw !== 'string' || raw.length === 0) {
          continue;
        }

        const mimeType =
          this.mimeFromHeaders(res.headers ?? {}) ||
          this.mimeFromBase64(raw) ||
          'image/jpeg';
        const base64 = this.normalizeBase64(raw);
        return `data:${mimeType};base64,${base64}`;
      } catch {
        /* try next responseType */
      }
    }

    try {
      const blob = await this.fetchAvatarBlobFallback(url);
      if (blob) {
        return await this.blobToDataUrl(blob);
      }
    } catch (error) {
      console.error('fetchAvatarBlobFallback failed', error);
    }

    return this.tryParseJsonAvatarResponse(url);
  }

  /** Web fallback when CapacitorHttp binary responseType is unavailable. */
  private async fetchAvatarBlobFallback(url: string): Promise<Blob | null> {
    const token = this.authService.getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
      return null;
    }
    return response.blob();
  }

  private async tryParseJsonAvatarResponse(url: string): Promise<string | null> {
    try {
      const resp = (await this.authHttpService.authenticatedGet({
        url,
      })) as ReadAvatarJsonResponse;

      const content = resp?.resource?.content;
      if (!content) {
        return null;
      }

      const mimeType = resp.resource?.mimeType ?? 'image/png';
      return `data:${mimeType};base64,${content}`;
    } catch {
      return null;
    }
  }

  private normalizeBase64(raw: string): string {
    let value = raw.trim().replace(/\s/g, '');
    const comma = value.indexOf(',');
    if (value.startsWith('data:') && comma !== -1) {
      value = value.slice(comma + 1);
    }
    const mod = value.length % 4;
    if (mod > 0) {
      value += '='.repeat(4 - mod);
    }
    return value;
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private mimeFromHeaders(headers: Record<string, string>): string {
    const key = Object.keys(headers ?? {}).find(
      (k) => k.toLowerCase() === 'content-type'
    );
    const raw = key ? headers[key] : '';
    return (raw?.split(';')[0] ?? '').trim();
  }

  private mimeFromBase64(base64: string): string {
    try {
      const normalized = this.normalizeBase64(base64).slice(0, 16);
      const bytes = atob(normalized)
        .slice(0, 4)
        .split('')
        .map((c) => c.charCodeAt(0));

      if (bytes[0] === 0xff && bytes[1] === 0xd8) {
        return 'image/jpeg';
      }
      if (
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47
      ) {
        return 'image/png';
      }
    } catch {
      /* ignore decode errors */
    }
    return '';
  }

  /**
   * Use Angular HttpClient so Capacitor's patched XHR handles multipart
   * with real File objects (plain Blob values are corrupted on native).
   */
  private async postUploadForm(url: string, formData: FormData): Promise<unknown> {
    if (
      !this.authService.hasStoredSession() ||
      !this.authService.getAccessToken()
    ) {
      await this.authService.bootstrapSession();
    }

    const headers = new HttpHeaders({
      Accept: 'application/json',
    });

    try {
      return await firstValueFrom(
        this.http.post(url, formData, { headers })
      );
    } catch (error) {
      console.error('Avatar upload request failed', error);
      throw error;
    }
  }
}
